import { useTranslation } from 'react-i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { formatDay, zonedDay } from '../logistics.format';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { buildRowClickStockDialog } from '../logisticsStockDialog';
import { DetailField as Field } from './DetailField';
import { DetailTabs } from './DetailTabs';
import type { IStockEntryFormProps } from './StockEntryDialog.types';
import { StockEntryForm } from './StockEntryForm';
import { StockBadge } from './InventoryRowCore';
/* The delivery drawer's shell verbatim — head/tabs/fields are the ONE
   record-drawer look, so the stylesheet is shared rather than mirrored. The
   header badge is the exception: it is the inventory row's own `StockBadge`,
   so the drawer and the row can never disagree about a material's state. */
import styles from './DeliveryDetailDrawer.module.css';

/** The form plumbing the page already hands the stock dialog — the Edit tab
 *  renders the same `StockEntryForm`, so it needs the same inputs. Exported
 *  for the surfaces that host `StockDetailBody` elsewhere (the site-map
 *  AreaDrawer's stock companion). */
export type StockFormPlumbing = Pick<
  IStockEntryFormProps,
  | 'siteId'
  | 'siteMedianLeadDays'
  | 'leadsByMaterial'
  | 'unitsByMaterial'
  | 'deliverySuggestions'
  | 'deliveredAmounts'
>;

interface IStockDetailDrawerProps extends StockFormPlumbing {
  /** The record to show. During a close this is the page's HELD copy — the
   *  live one may already be gone (a delete) while the exit still needs
   *  something to slide out. Null only before the drawer has ever opened. */
  level: InventoryLevel | null;
  /** Whether the drawer is open. The component stays MOUNTED when this flips
   *  false — Base UI plays the slide-out and unmounts the panel itself. */
  open: boolean;
  onClose: () => void;
  /** The PROJECT's IANA timezone — the calendar every day-level reading here
   *  is measured against. */
  tz: string;
  /** Stages the delete — the page's DiscardModal is what actually fires
   *  (deletes here are irreversible). On success the record disappears and
   *  the drawer closes itself by having nothing to show. */
  onDelete: (level: InventoryLevel) => void;
  /** The caller's role may write (the page's `canEdit`). When false there is
   *  no Edit tab: the drawer is the Overview alone. */
  canEdit: boolean;
  /** Told when the Edit tab has been opened (a form is now mounted, holding
   *  input) and again when it goes away. */
  onEditVisitedChange?: (visited: boolean) => void;
}

/**
 * One stock material's record, in the delivery drawer's shape: an Overview tab
 * reading the running estimate in full, and an Edit tab hosting the same
 * `StockEntryForm` the stock dialog wraps — one form, two surfaces. Actions
 * live HERE rather than on the row's kebab (removed with this drawer, the same
 * move the delivery cards made 2026-08-20) so a row has exactly one affordance
 * — open the record — and the record is where you act on it. Delete exists
 * only on the Edit tab, in the footer slot the dialog uses for Cancel.
 */
export function StockDetailDrawer({
  level,
  open,
  onClose,
  tz,
  onDelete,
  canEdit,
  onEditVisitedChange,
  ...plumbing
}: IStockDetailDrawerProps) {
  const { t } = useTranslation();
  return (
    <Drawer
      open={open && level !== null}
      ariaLabel={
        level ? `${t('logistics.stockDetail.title')} ${level.materialName}` : t('logistics.stockDetail.title')
      }
      closeLabel={t('logistics.stockDetail.close')}
      onClose={onClose}
    >
      {/* The body lives INSIDE the popup, keyed by the record, so every
          per-record state it holds (`editVisited`, Tabs' active value, the
          form's fields) dies with Base UI's post-exit unmount and can never
          leak into the next record or the next open. */}
      {level ? (
        <StockDetailBody
          key={level.materialName}
          level={level}
          tz={tz}
          onClose={onClose}
          onDelete={onDelete}
          canEdit={canEdit}
          onEditVisitedChange={onEditVisitedChange}
          {...plumbing}
        />
      ) : null}
    </Drawer>
  );
}

type IStockDetailBodyProps = Omit<IStockDetailDrawerProps, 'open' | 'level'> & {
  level: InventoryLevel;
};

/** Exported for the AreaDrawer's stock companion, which shows the same
 *  record body docked beside the area drawer instead of in its own Drawer. */
export function StockDetailBody({
  level,
  tz,
  onClose,
  onDelete,
  canEdit,
  onEditVisitedChange,
  ...plumbing
}: IStockDetailBodyProps) {
  const { t, i18n } = useTranslation();
  const { saveLocked, locked } = useLogisticsEditLock();
  const dash = t('logistics.detail.empty');
  // A stored unit (a key, or a label saved in either language) in the viewer's.
  const unit = unitLabel(t, level.unit);

  return (
    <>
      <header className={styles.head}>
        <span className={styles.eyebrow}>{t('logistics.stockDetail.title')}</span>
        <h2 className={styles.title}>{level.materialName}</h2>
        {/* The row's own badge, unabridged: a ran-out estimate reads "Ran out",
            never "0d left" (spec §3 honesty rules), and the tone is the row's
            (`rowTone`) — calm while a replacement is in flight, amber for a
            late ETA — so list and drawer always agree. */}
        <StockBadge level={level} tz={tz} />
      </header>

      <DetailTabs
        editDisabled={locked}
        onEditVisitedChange={onEditVisitedChange}
        overview={
          <dl className={styles.fields}>
            <Field label={t('logistics.stockDialog.amount')}>{`${level.amount} ${unit}`}</Field>
            <Field label={t('logistics.stockDialog.lasts')}>
              {`${level.spanDays} ${t('logistics.stockDialog.days')}`}
            </Field>
            <Field label={t('logistics.stockDetail.rate')}>
              {t('logistics.inventory.perDay', { rate: level.dailyRate, unit })}
            </Field>
            {/* enteredAt is an ISO instant — resolve it to the PROJECT's
                calendar day before formatting, like every instant here. */}
            <Field label={t('logistics.stockDetail.started')}>
              {formatDay(zonedDay(level.enteredAt, tz), i18n.language)}
            </Field>
            <Field label={t('logistics.stockDetail.enteredBy')}>{level.enteredBy}</Field>
            <Field label={t('logistics.stockDialog.leadLabel')}>
              {level.leadTimeDays != null
                ? t('logistics.inventory.deliveryTakes', { n: level.leadTimeDays })
                : dash}
            </Field>
          </dl>
        }
        edit={
          !canEdit ? undefined : (
            /* The stock dialog's form, inline — mounted lazily by `DetailTabs`
               on the first Edit visit. NOT keyed on the record's content:
               `InventoryLevel` carries derived fields (`daysLeft`, `overdue`,
               the arrival flags) that any refetch can change, and a content
               key would remount mid-edit and silently discard typing. The
               body's own `key={level.materialName}` already gives the form a
               fresh mount per record, and a landed save closes the drawer, so
               there is no stale-prefill window to guard. */
            <StockEntryForm
              {...plumbing}
              prefill={buildRowClickStockDialog(level).prefill}
              /* A landed save closes the drawer, same as the delivery
                 drawer's Edit tab — the record is written, the row is where
                 it shows. */
              onClose={onClose}
              /* Delete rides the footer slot the dialog uses for Cancel —
                 the one destructive act, only reachable in edit mode. It
                 stages; the page's modal destroys. */
              secondaryAction={
                <button
                  type="button"
                  className={styles.deleteBtn}
                  aria-disabled={saveLocked || undefined}
                  onClick={saveLocked ? undefined : () => onDelete(level)}
                >
                  {t('logistics.inventory.menu.delete')}
                </button>
              }
            />
          )
        }
      />
    </>
  );
}
